import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, CaretLeft, CaretRight, Lightning, DownloadSimple, Gear, Palette, DotsThree, FileXls, FilePdf, List } from '@phosphor-icons/react';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function getTextColor(bgColor) {
  if (!bgColor || bgColor === '#FFFFFF') return '#000000';
  const hex = bgColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 155 ? '#000000' : '#FFFFFF';
}

export default function MonthlySchedulePage() {
  const { fleetId } = useParams();
  const navigate = useNavigate();
  const [fleet, setFleet] = useState(null);
  const [crew, setCrew] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [dutyCodes, setDutyCodes] = useState([]);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const actionsMenuRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [fleetRes, crewRes, schedRes, codesRes] = await Promise.all([
        axios.get(`${API_URL}/api/fleets/${fleetId}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/crew-members?fleet_id=${fleetId}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/schedules/${fleetId}/${year}/${month}`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/duty-codes?fleet_id=${fleetId}`, { withCredentials: true })
      ]);
      setFleet(fleetRes.data);
      setCrew(crewRes.data);
      setSchedules(schedRes.data);
      setDutyCodes(codesRes.data);
    } finally {
      setLoading(false);
    }
  }, [fleetId, month, year, setFleet, setCrew, setSchedules, setDutyCodes, setLoading]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(event.target)) {
        setShowActionsMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleExportExcel = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/schedules/${fleetId}/${year}/${month}/export/excel`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `schedule_${fleet?.name}_${year}_${month}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel exported successfully');
      setShowActionsMenu(false);
    } catch (error) {
      toast.error('Failed to export Excel');
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/schedules/${fleetId}/${year}/${month}/export/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `schedule_${fleet?.name}_${year}_${month}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF exported successfully');
      setShowActionsMenu(false);
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  const handleGenerate = async () => {
    if (crew.length === 0) {
      toast.error('Add crew members first');
      return;
    }
    setGenerating(true);
    try {
      await axios.post(`${API_URL}/api/schedules/generate`, { fleet_id: fleetId, month, year }, { withCredentials: true });
      toast.success('Schedule generated');
      fetchData();
    } catch (error) {
      toast.error('Failed to generate schedule');
    } finally {
      setGenerating(false);
    }
  };

  const handleCellChange = async (scheduleId, dayIndex, newDuty) => {
    try {
      const schedule = schedules.find(s => s.id === scheduleId);
      const updatedCells = [...schedule.cells];
      updatedCells[dayIndex].duty_type = newDuty;
      await axios.put(`${API_URL}/api/schedules/${scheduleId}`, { crew_member_id: schedule.crew_member_id, cells: updatedCells }, { withCredentials: true });
      fetchData();
      setEditingCell(null);
      toast.success('Updated');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleExport = async (format) => {
    try {
      const response = await axios.get(`${API_URL}/api/schedules/${fleetId}/${year}/${month}/export/${format}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `schedule_${year}_${month}.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const getDaysInMonth = () => new Date(year, month, 0).getDate();
  const monthName = new Date(year, month - 1).toLocaleString('default', { month: 'long' });

  const prevMonth = () => {
    if (month === 1) {
      setMonth(12);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };

  const nextMonth = () => {
    if (month === 12) {
      setMonth(1);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  const getDutyCodeInfo = (code) => {
    const dutyCode = dutyCodes.find(dc => dc.code === code);
    return dutyCode || { code, label: code, color: '#9CA3AF' };
  };

  const getCounts = (schedule) => {
    const counts = {};
    if (!schedule) return counts;
    schedule.cells.forEach(cell => {
      counts[cell.duty_type] = (counts[cell.duty_type] || 0) + 1;
    });
    return counts;
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="w-16 h-16 border-4 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div></div>;
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Sidebar isOpen={showSidebar} onClose={() => setShowSidebar(false)} fleetId={fleetId} />
      
      <div className="w-full px-4 py-6">
        <div className="flex items-center gap-4 mb-6">
          <button 
            onClick={() => setShowSidebar(true)} 
            className="p-2 hover:bg-gray-100 rounded-sm"
            title="Open Menu"
          >
            <List size={24} weight="bold" />
          </button>
          <button onClick={() => navigate('/fleets')} className="p-2 hover:bg-gray-100 rounded-sm">
            <ArrowLeft size={24} />
          </button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-[#111827]">{fleet?.name} - {monthName} {year}</h1>
          </div>
          <div className="relative" ref={actionsMenuRef}>
            <button 
              onClick={() => setShowActionsMenu(!showActionsMenu)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-[#E5E7EB] rounded-sm hover:bg-gray-50"
            >
              <DotsThree size={20} weight="bold" /> Actions
            </button>
            {showActionsMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white border border-[#E5E7EB] rounded-sm shadow-lg z-50">
                <div className="py-1">
                  <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Extract Files</div>
                  <button
                    onClick={handleExportExcel}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <FileXls size={18} className="text-green-600" />
                    <span>Export as Excel</span>
                  </button>
                  <button
                    onClick={handleExportPDF}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <FilePdf size={18} className="text-red-600" />
                    <span>Export as PDF</span>
                  </button>
                </div>
              </div>
            )}
          </div>
          <button onClick={() => navigate(`/fleet/${fleetId}/codes`)} className="flex items-center gap-2 px-4 py-2 bg-white border border-[#E5E7EB] rounded-sm hover:bg-gray-50">
            <Palette size={20} /> Duty Codes
          </button>
          <button onClick={() => navigate(`/fleet/${fleetId}/rules`)} className="flex items-center gap-2 px-4 py-2 bg-white border border-[#E5E7EB] rounded-sm hover:bg-gray-50">
            <Gear size={20} /> Rules
          </button>
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm mb-4 p-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button onClick={prevMonth} className="p-2 hover:bg-gray-100 rounded-sm">
                <CaretLeft size={20} />
              </button>
              <div className="flex items-center gap-2">
                <select value={month} onChange={(e) => setMonth(parseInt(e.target.value))} className="px-3 py-2 border border-[#E5E7EB] rounded-sm">
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                    <option key={m} value={m}>{new Date(2024, m - 1).toLocaleString('default', { month: 'long' })}</option>
                  ))}
                </select>
                <select value={year} onChange={(e) => setYear(parseInt(e.target.value))} className="px-3 py-2 border border-[#E5E7EB] rounded-sm">
                  {Array.from({ length: 5 }, (_, i) => 2024 + i).map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <button onClick={nextMonth} className="p-2 hover:bg-gray-100 rounded-sm">
                <CaretRight size={20} />
              </button>
            </div>

            <div className="flex gap-2">
              <button onClick={handleGenerate} disabled={generating} className="flex items-center gap-2 px-4 py-2 bg-[#002FA7] text-white rounded-sm hover:bg-[#001f7a] disabled:opacity-50">
                <Lightning size={18} /> {generating ? 'Generating...' : 'Generate'}
              </button>
              <button onClick={() => handleExport('excel')} className="flex items-center gap-2 px-4 py-2 bg-[#00B368] text-white rounded-sm hover:bg-[#009554]">
                <DownloadSimple size={18} /> Excel
              </button>
              <button onClick={() => handleExport('pdf')} className="flex items-center gap-2 px-4 py-2 bg-[#FF2A2A] text-white rounded-sm hover:bg-[#d62323]">
                <DownloadSimple size={18} /> PDF
              </button>
            </div>
          </div>
        </div>

        {/* Duty Code Legend */}
        <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm mb-4 p-4">
          <h3 className="text-sm font-semibold text-[#111827] mb-3">Duty Code Legend</h3>
          <div className="flex flex-wrap gap-3">
            {dutyCodes.map(code => (
              <div key={code.id} className="flex items-center gap-2">
                <div className="w-6 h-6 rounded border border-gray-300" style={{ backgroundColor: code.color }}></div>
                <span className="text-sm font-medium">{code.code}</span>
                <span className="text-xs text-gray-500">- {code.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className="bg-gray-50 text-left text-xs font-semibold text-[#111827] uppercase border border-[#E5E7EB] p-2 sticky left-0 z-10 min-w-[180px]">Crew Member</th>
                {Array.from({ length: getDaysInMonth() }, (_, i) => i + 1).map(day => (
                  <th key={`day-${day}`} className="bg-gray-50 text-center text-xs font-semibold text-[#111827] border border-[#E5E7EB] p-2 min-w-[50px]">{day}</th>
                ))}
                <th className="bg-gray-50 text-center text-xs font-semibold text-[#111827] uppercase border border-[#E5E7EB] p-2 min-w-[80px]">Summary</th>
              </tr>
            </thead>
            <tbody>
              {crew.map((member) => {
                const schedule = schedules.find(s => s.crew_member_id === member.id);
                const counts = getCounts(schedule);
                return (
                  <tr key={member.id}>
                    <td className="p-2 border border-[#E5E7EB] bg-white sticky left-0 z-10">
                      <div className="flex items-center gap-2">
                        {member.photo_id ? (
                          <img src={`${API_URL}/api/photos/${member.photo_id}`} alt={member.name} className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gray-200"></div>
                        )}
                        <div>
                          <div className="font-medium text-[#111827]">{member.name}</div>
                          <div className="text-xs text-[#6B7280]">{member.rank}</div>
                        </div>
                      </div>
                    </td>
                    {schedule ? schedule.cells.map((cell, idx) => {
                      const dutyInfo = getDutyCodeInfo(cell.duty_type);
                      const isEditing = editingCell?.scheduleId === schedule.id && editingCell?.dayIndex === idx;
                      const textColor = getTextColor(dutyInfo.color);
                      return (
                        <td key={`${schedule.id}-${cell.date}`} className="p-1 border border-[#E5E7EB] text-center">
                          {isEditing ? (
                            <select autoFocus value={cell.duty_type} onChange={(e) => handleCellChange(schedule.id, idx, e.target.value)} onBlur={() => setEditingCell(null)} className="w-full px-1 py-1 text-xs border border-[#002FA7] rounded-sm">
                              {dutyCodes.map(dc => <option key={dc.id} value={dc.code}>{dc.code}</option>)}
                            </select>
                          ) : (
                            <button onClick={() => setEditingCell({ scheduleId: schedule.id, dayIndex: idx })} className="w-full px-2 py-1 rounded-sm text-xs font-bold hover:opacity-80" style={{ backgroundColor: dutyInfo.color, color: textColor }}>
                              {dutyInfo.code}
                            </button>
                          )}
                        </td>
                      );
                    }) : (
                      Array.from({ length: getDaysInMonth() }).map((_, idx) => <td key={`empty-${member.id}-${idx}`} className="p-2 border border-[#E5E7EB] text-center text-[#9CA3AF]">-</td>)
                    )}
                    <td className="p-2 border border-[#E5E7EB] text-center bg-[#F3F4F6]">
                      <div className="text-xs">
                        {Object.entries(counts).map(([code, count]) => (
                          <div key={code}>{code}: {count}</div>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
